import React from 'react';
import Moment from 'moment';
import { withTranslation } from 'react-i18next';
import Datagrid from '../../../UI/Datagrid';
import ActionsDropdown from '../helpers/ActionsDropdown';
import { DisableModal, EnableModal } from '../helpers/SharedModal';
import helper from '../../../../utils/helper';
import { Alert } from '../../../UI/Common';
import { connect } from 'react-redux';
import { cloneDeep, isEmpty } from 'lodash';
import { useHistory } from 'react-router';

const dateFormat = 'MM/DD/yyyy HH:mm:ss';
function toUTC(dt) {
  return Moment.utc(dt, dateFormat).format();
}
const CurrentMeasurementValue = ({ t, ...props }) => {
  const { isFetching, isCompleted, sensors, groupId } = props;
  const history = useHistory();
  function isMapAvailable() {
    const { availableGroups } = props.dashboard;
    if (availableGroups.length) {
      const result = availableGroups.filter(
        (group) => group.groupId.toString() === groupId,
      )[0];
      if (result) {
        if (result.maps) {
          return [result.maps, result.mapDepartmentId];
        }
        return [];
      }
      return [];
    }
    return [];
  }
  const locale = localStorage.getItem('language') || 'en_US';
  const columns = [
    {
      dataField: 'location',
      text: 'Location',
      sort: true,
    },
    {
      dataField: 'name',
      text: 'Name',
      sort: true,
    },
    {
      dataField: 'parameter',
      text: 'Parameter',
      sort: true,
    },
    {
      dataField: 'textValue',
      text: 'Value',
      sort: true,
    },
    {
      dataField: 'units',
      text: 'Units',
      sort: true,
    },
    {
      dataField: 'alarmImage',
      text: 'Status',
      sort: true,
      formatter: formatterStatus,
    },
    {
      dataField: 'timeStamp',
      text: 'Time Last Updated',
      sort: true,
      isDateTime: true,
      headerStyle: { width: 150 },
    },
  ];
  const defaultSorted = [
    {
      dataField: 'name',
      order: 'asc',
    },
  ];

  function formatterStatus(cell) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <img
          src={process.env.PUBLIC_URL + `/img/statusIcon/${cell}`}
          width={24}
          height={24}
          alt="status-icon"
          title={t(helper.getStatusIconName(cell))}
        />
      </div>
    );
  }

  const [selectedCmv, setSelectedCmv] = React.useState([]);
  const [selectedSensor, setSensor] = React.useState(null);
  const [isDisabling, setIsDisabling] = React.useState(false);
  const [isEnabling, setIsEnabling] = React.useState(false);

  React.useEffect(() => {
    if (isCompleted) {
      setSelectedCmv([]);
    }
  }, [isCompleted]);

  const handleOnSelectCmv = (row, isSelect) => {
    if (isSelect) {
      setSelectedCmv([...selectedCmv, row.sensorId]);
    } else {
      setSelectedCmv(selectedCmv.filter((x) => x !== row.sensorId));
    }
  };

  const handleOnSelectAllCmv = (isSelect, rows) => {
    const ids = rows.map((r) => r.sensorId);
    if (isSelect) {
      setSelectedCmv((selectedCmv) => [
        ...selectedCmv,
        ...ids.filter((id) => !selectedCmv.includes(id)),
      ]);
    } else {
      setSelectedCmv((selectedCmv) =>
        selectedCmv.filter((sensorId) => !ids.includes(sensorId)),
      );
    }
  };

  const selectRow = {
    mode: 'checkbox',
    clickToSelect: true,
    selected: selectedCmv,
    onSelect: handleOnSelectCmv,
    onSelectAll: handleOnSelectAllCmv,
  };

  const showMap = () => {
    const [mapName, mapDepartmentId] = isMapAvailable();
    history.push(
      `/group-map?mapName=${mapName}&mapDepartmentId=${mapDepartmentId}`,
    );
  };
  const showTrendChart = (sensorId) => {
    function getTrendChartDefaultPeriodHours() {
      const trendChartDefaultPeriodHours = helper.getSetting(
        'TrendChartDefaultPeriodHours',
      );
      return parseInt(trendChartDefaultPeriodHours.value);
    }
    const hours = getTrendChartDefaultPeriodHours();
    const toDate = toUTC(Moment().toDate());
    const fromDate = toUTC(Moment().subtract(hours, 'hours').toDate());
    history.push(
      `/trend-charts?sensorIds=${
        sensorId ? sensorId : selectedCmv.join(',')
      }&fromDate=${fromDate}&toDate=${toDate}&locale=${locale}`,
    );
  };

  const cmvActions = [
    {
      label: 'Show map',
      action: showMap,
      disabled: !isMapAvailable().length,
    },
    {
      label: 'Add note',
      action: () =>
        history.push(`/add-note?sensorIds=${selectedCmv.join(',')}`),
      disabled: !(selectedCmv.length && helper.checkPermission('API.Notes')),
    },
    {
      label: 'Show trend chart',
      action: () => showTrendChart(),
      disabled: !selectedCmv.length,
    },
    {
      label: 'Sensor disable',
      action: () => setIsDisabling(true),
      disabled: !(
        selectedCmv.length && helper.checkPermission('API.Sensors.Disable')
      ),
    },
    {
      label: 'Sensor enable',
      action: () => setIsEnabling(true),
      disabled: !(
        selectedCmv.length && helper.checkPermission('API.Sensors.Enable')
      ),
    },
    {
      label: 'Sensor info',
      action: () =>
        history.push(`/sensor-info?sensorIds=${selectedCmv.join(',')}`),
      disabled:
        !selectedCmv.length ||
        (!helper.checkPermission('Admin') &&
          !helper.checkPermission('Group Administrator')),
    },
    // {
    //   label: 'Edit monitoring point',
    //   disabled: true,
    //   action: (row) =>
    //     history.push(
    //       `/configure/groups/edit-sensor?groupId=${groupId}&sensorId=${row.sensorId}`,
    //     ),
    // },
    // {
    //   label: 'Edit sensor type settings',
    //   disabled: true,
    //   action: (row) =>
    //     history.push(
    //       `/configure/sensor-types/edit-sensor-type/${row.sensorTypeId}`,
    //     ),
    // },
    {
      label: 'Edit group',
      action: (row) => history.push(`/configure/groups/edit-group/${groupId}`),
      disabled: !helper.checkPermission('API.GroupSetting'),
    },
    {
      label: 'Maintain database',
      action: (row) => history.push(`/configure/groups/maintain-db/${groupId}`),
      disabled: !helper.checkPermission('API.GroupSetting'),
    },
  ];

  const ctxMenuOptions = [
    [
      {
        name: 'Show map',
        className: () => (isMapAvailable().length ? '' : 'item-disabled'),
        action: showMap,
      },
      {
        name: 'Add note',
        className: () =>
          helper.checkPermission('API.Notes') ? '' : 'item-disabled',
        action: (row) =>
          history.push(
            `/add-note?sensorIds=${row.sensorId}&sensorName=${row.name}`,
          ),
      },
      {
        name: 'Show trend chart',
        action: (row) => showTrendChart(row.sensorId),
      },
      {
        name: 'Sensor disable',
        action: (row) => {
          setSensor(row.sensorId.toString());
          setIsDisabling(true);
        },
        className: () =>
          helper.checkPermission('API.Sensors.Disable') ? '' : 'item-disabled',
      },
      {
        name: 'Sensor enable',
        action: (row) => {
          setSensor(row.sensorId.toString());
          setIsEnabling(true);
        },
        className: () =>
          helper.checkPermission('API.Sensors.Enable') ? '' : 'item-disabled',
      },
      {
        name: 'Sensor info',
        className: (row) =>
          helper.checkPermission('Admin') ||
          helper.checkPermission('Group Administrator')
            ? ''
            : 'item-disabled',
        action: (row) => history.push(`/sensor-info?sensorIds=${row.sensorId}`),
      },
      {
        name: 'Edit monitoring point',
        className: (row) =>
          helper.checkPermission('Admin') ||
          helper.checkPermission('Group Administrator')
            ? ''
            : 'item-disabled',
        action: (row) =>
          history.push(
            `/configure/groups/edit-sensor?groupId=${groupId}&sensorId=${row.sensorId}`,
          ),
      },
      {
        name: 'Edit sensor type settings',
        className: (row) =>
          helper.checkPermission('API.SensorTypes') ? '' : 'item-disabled',
        action: (row) =>
          history.push(
            `/configure/sensor-types/edit-sensor-type/${row.sensorTypeId}`,
          ),
      },
      {
        name: 'Edit group',
        className: (row) =>
          helper.checkPermission('API.GroupSetting') ? '' : 'item-disabled',
        action: (row) =>
          history.push(`/configure/groups/edit-group/${groupId}`),
      },
      {
        name: 'Maintain database',
        className: (row) =>
          helper.checkPermission('API.GroupSetting') ? '' : 'item-disabled',
        action: (row) =>
          history.push(`/configure/groups/maintain-db/${groupId}`),
      },
    ],
  ];

  function onCancelModal(type = 'disable') {
    if (type === 'disable') {
      setIsDisabling(false);
    } else {
      setIsEnabling(false);
    }
    setSensor(null);
    setSelectedCmv([]);
  }
  function onDisableSensor(params) {
    let sensorIds = selectedCmv.join(',');
    if (selectedSensor) {
      sensorIds = selectedSensor;
    }

    setIsDisabling(false);
    props.onDisableSensor({ sensorIds, ...params });
    setSensor(null);
  }

  function onEnableSensor(dateTime) {
    let sensorIds = selectedCmv.join(',');
    if (selectedSensor) {
      sensorIds = [selectedSensor];
    }
    setIsEnabling(false);
    props.onEnableSensor(sensorIds);
    setSensor(null);
  }
  const summaries = cloneDeep(props.dashboard.availableGroupsSummaries);
  function checkSummaries() {
    return isEmpty(summaries) && isEmpty(summaries[0]);
  }

  return (
    <div>
      <DisableModal
        isOpen={isDisabling}
        onCancel={() => onCancelModal('disable')}
        onConfirm={onDisableSensor}
      />
      <EnableModal
        isOpen={isEnabling}
        onCancel={() => onCancelModal('enable')}
        onConfirm={onEnableSensor}
      />
      <Alert.Error text={props.errors} />
      <div className="d-flex justify-content-between align-items-center">
        <h1>{t('Current Measurement Values')}</h1>
        {/* <ActionsDropdown options={cmvActions} /> */}
        <div className="d-flex justify-content-end align-items-end">
          {props.groupId && !checkSummaries() && (
            <div
              className="d-flex"
              style={{
                marginRight: 10,
                padding: 5,
              }}
            >
              <div>
                <span style={{ fontWeight: 600 }}>
                  {t('Sensors')}: {summaries[0].numSensors},{' '}
                </span>
                <span style={{ fontWeight: 600 }}>
                  {t('Errors')}: {summaries[0].numErrors},{' '}
                </span>
                <span style={{ fontWeight: 600 }}>
                  {t('Alarms')}: {summaries[0].numAlarms},{' '}
                </span>
                <span style={{ fontWeight: 600 }}>
                  {t('Warnings')}: {summaries[0].numWarnings},{' '}
                </span>
                <span style={{ fontWeight: 600 }}>
                  {t('Overriden')}: {summaries[0].numOverriden}
                </span>
              </div>
            </div>
          )}
          <ActionsDropdown options={cmvActions} />
        </div>
      </div>
      <Datagrid
        datagridProps={{
          keyField: 'sensorId',
          data: sensors,
          columns,
          defaultSorted,
          selectRow,
          loading: isFetching,
        }}
        classes="mobile-datagrid-responsive"
        ctxMenuId="CMV-context"
        ctxMenuOptions={ctxMenuOptions}
        exportProps={{
          fileName: `AlarmList-${Moment().format('MM-DD-YYYY')}.csv`,
        }}
      />
    </div>
  );
};

function mapStateToProps(state) {
  return {
    dashboard: state.dashboard,
  };
}

export default withTranslation('app')(
  connect(mapStateToProps, null)(CurrentMeasurementValue),
);
